import './style.scss'
import React, { useEffect, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'

import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Gapcursor from '@tiptap/extension-gapcursor'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align'

import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import Underline from '@tiptap/extension-underline'
import Heading from '@tiptap/extension-heading'
import Blockquote from '@tiptap/extension-blockquote'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import HardBreak from '@tiptap/extension-hard-break'

import TextStyle from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import FontFamily from '@tiptap/extension-font-family'
import withMainLayout from '../../hocs/withMainLayout'
import { useAuth } from '../../context/useAuth'
import { axiosKazlingoJson } from '../../utils/axiosKazlingo'

function Editor() {
  const { user } = useAuth()
  const [entries, setEntries] = useState([])
  const [modules, setModules] = useState([])
  const [sections, setSections] = useState([])
  const [selectedEntry, setSelectedEntry] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  // Язык редактируемой версии грамматики: ru → textInHtml, en → textInHtmlEn
  const [editLang, setEditLang] = useState('ru')
  const editLangRef = useRef('ru')
  const htmlKeyFor = lang => (lang === 'en' ? 'textInHtmlEn' : 'textInHtml')
  const [newEntry, setNewEntry] = useState({
    moduleId: '',
    sectionId: '',
    buttonId: ''
  })

  useEffect(() => {
    async function fetchModules() {
      try {
        const { data } = await axiosKazlingoJson.get('/modules', {
          headers: { userid: user.id }
        })
        const sections = data.flatMap(module => module.sections)

        setModules(data)
        setSections(sections)
      } catch (error) {
        console.error('Error fetching modules:', error)
      }
    }
    if (user) {
      fetchModules()
    }
  }, [user])

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Gapcursor,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Bold,
      Italic,
      Strike,
      Underline,
      Heading.configure({ levels: [1, 2, 3] }),
      Blockquote,
      BulletList,
      OrderedList,
      ListItem,
      HardBreak,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Highlight,
      Text,
      TextStyle,
      Color,
      FontFamily
    ],
    content: '<p> АльФараби гори в бане! </p>',
    onUpdate: ({ editor }) => {
      if (selectedEntry) {
        setSelectedEntry(prev => ({
          ...prev,
          [htmlKeyFor(editLangRef.current)]: editor.getHTML()
        }))
      }
    }
  })

  const getHtmlContent = () => {
    if (editor) {
      console.log(editor.getHTML())
    }
  }

  useEffect(() => {
    async function fetchEntries() {
      try {
        const response = await axiosKazlingoJson.get('/grammar-text')
        setEntries(response.data)
      } catch (error) {
        console.error('Error fetching text entries:', error)
      }
    }
    fetchEntries()
  }, [])

  const handleSelectEntry = entry => {
    setSelectedEntry(entry)
    editor?.commands.setContent(entry[htmlKeyFor(editLangRef.current)] || '')
  }

  const switchEditLang = lang => {
    editLangRef.current = lang
    setEditLang(lang)
    if (selectedEntry) {
      editor?.commands.setContent(selectedEntry[htmlKeyFor(lang)] || '')
    }
  }

  const saveContent = async () => {
    if (!editor) return
    setIsSaving(true)

    console.log('newEntry', newEntry, editor)

    try {
      if (selectedEntry && selectedEntry._id) {
        await axiosKazlingoJson.put(`/grammar-text/${selectedEntry._id}`, {
          [htmlKeyFor(editLang)]: editor.getHTML(),
          ...(editLang === 'en' && { titleEn: selectedEntry.titleEn || '' })
        })
        setEntries(
          entries.map(e =>
            e._id === selectedEntry._id
              ? {
                  ...e,
                  [htmlKeyFor(editLang)]: editor.getHTML(),
                  titleEn: selectedEntry.titleEn
                }
              : e
          )
        )
        alert('Content updated successfully!')
      } else if (editLang === 'en') {
        alert('Сначала создайте русскую версию, затем добавьте английскую.')
      } else {
        const response = await axiosKazlingoJson.post(`/grammar-text`, {
          ...newEntry,
          textInHtml: editor.getHTML()
        })
        setEntries([...entries, response.data])
        setSelectedEntry(response.data)
        alert('Content created successfully!')
      }
    } catch (error) {
      console.error('Error saving content:', error)
      alert('Failed to save content.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="editor-container">
      <h3>Созданные тексты</h3>
      <div
        className="sidebar"
        style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '30px' }}
      >
        <ul>
          {entries.map(entry => (
            <li
              key={entry._id}
              style={{ marginBottom: '10px', listStyleType: 'none' }}
            >
              <button
                onClick={() => handleSelectEntry(entry)}
                style={{
                  backgroundColor: '#007BFF',
                  color: 'white',
                  border: 'none',
                  padding: '10px 15px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  borderRadius: '5px',
                  textAlign: 'center',
                  transition: 'background-color 0.3s ease',
                  minWidth: '300px'
                }}
              >
                {entry.moduleName} - {entry.sectionName} - {entry.buttonOrder}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="editor-content">
        <div style={{ marginBottom: '10px' }}>
          <b>Язык версии: </b>
          <label style={{ marginRight: '10px' }}>
            <input
              type="radio"
              checked={editLang === 'ru'}
              onChange={() => switchEditLang('ru')}
            />{' '}
            Русский (textInHtml)
          </label>
          <label>
            <input
              type="radio"
              checked={editLang === 'en'}
              onChange={() => switchEditLang('en')}
            />{' '}
            English (textInHtmlEn)
          </label>
          {editLang === 'en' && selectedEntry && (
            <input
              type="text"
              placeholder="titleEn"
              style={{ marginLeft: '10px', minWidth: '300px' }}
              value={selectedEntry.titleEn || ''}
              onChange={e =>
                setSelectedEntry(prev => ({ ...prev, titleEn: e.target.value }))
              }
            />
          )}
        </div>
        <div className="input-fields">
          <select
            value={newEntry.moduleName}
            onChange={e =>
              setNewEntry({ ...newEntry, moduleName: e.target.value })
            }
          >
            <option value="">Select Module</option>
            {modules.map(module => (
              <option key={module._id} value={module.moduleName}>
                {module.moduleName}
              </option>
            ))}
          </select>
          <select
            value={newEntry.sectionName}
            onChange={e =>
              setNewEntry({ ...newEntry, sectionName: e.target.value })
            }
          >
            <option value="">Select Section</option>
            {sections.map(section => (
              <option key={section._id} value={module.sectionName}>
                {section.sectionName}
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Button ID"
            value={newEntry.buttonOrder}
            onChange={e =>
              setNewEntry({ ...newEntry, buttonOrder: e.target.value })
            }
          />
        </div>

        <EditorContent editor={editor} />

        <div className="editor-content">
          <div className="button-group">
            <input
              type="color"
              onInput={event =>
                editor
                  .chain()
                  .focus()
                  .setColor(event.target.value)
                  .run()
              }
              value={editor.getAttributes('textStyle').color}
              data-testid="setColor"
            />
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setColor('#958DF1')
                  .run()
              }
              className={
                editor.isActive('textStyle', { color: '#958DF1' })
                  ? 'is-active'
                  : ''
              }
              data-testid="setPurple"
            >
              Purple
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setColor('#F98181')
                  .run()
              }
              className={
                editor.isActive('textStyle', { color: '#F98181' })
                  ? 'is-active'
                  : ''
              }
              data-testid="setRed"
            >
              Red
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setColor('#FBBC88')
                  .run()
              }
              className={
                editor.isActive('textStyle', { color: '#FBBC88' })
                  ? 'is-active'
                  : ''
              }
              data-testid="setOrange"
            >
              Orange
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setColor('#FAF594')
                  .run()
              }
              className={
                editor.isActive('textStyle', { color: '#FAF594' })
                  ? 'is-active'
                  : ''
              }
              data-testid="setYellow"
            >
              Yellow
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setColor('#70CFF8')
                  .run()
              }
              className={
                editor.isActive('textStyle', { color: '#70CFF8' })
                  ? 'is-active'
                  : ''
              }
              data-testid="setBlue"
            >
              Blue
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setColor('#94FADB')
                  .run()
              }
              className={
                editor.isActive('textStyle', { color: '#94FADB' })
                  ? 'is-active'
                  : ''
              }
              data-testid="setTeal"
            >
              Teal
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setColor('#B9F18D')
                  .run()
              }
              className={
                editor.isActive('textStyle', { color: '#B9F18D' })
                  ? 'is-active'
                  : ''
              }
              data-testid="setGreen"
            >
              Green
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .unsetColor()
                  .run()
              }
              data-testid="unsetColor"
            >
              Unset color
            </button>
          </div>
          <div className="toolbar">
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleBold()
                  .run()
              }
            >
              Bold
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleItalic()
                  .run()
              }
            >
              Italic
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleUnderline()
                  .run()
              }
            >
              Underline
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleStrike()
                  .run()
              }
            >
              Strike
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 1 })
                  .run()
              }
            >
              H1
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 2 })
                  .run()
              }
            >
              H2
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: 3 })
                  .run()
              }
            >
              H3
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleBlockquote()
                  .run()
              }
            >
              Blockquote
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleBulletList()
                  .run()
              }
            >
              Bullet List
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleOrderedList()
                  .run()
              }
            >
              Ordered List
            </button>

            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHighlight()
                  .run()
              }
              className={editor.isActive('highlight') ? 'is-active' : ''}
            >
              Highlight
            </button>

            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHighlight()
                  .run()
              }
              className={editor.isActive('highlight') ? 'is-active' : ''}
            >
              Highlight
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setTextAlign('left')
                  .run()
              }
              className={
                editor.isActive({ textAlign: 'left' }) ? 'is-active' : ''
              }
            >
              Left
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setTextAlign('center')
                  .run()
              }
              className={
                editor.isActive({ textAlign: 'center' }) ? 'is-active' : ''
              }
            >
              Center
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setTextAlign('right')
                  .run()
              }
              className={
                editor.isActive({ textAlign: 'right' }) ? 'is-active' : ''
              }
            >
              Right
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setTextAlign('justify')
                  .run()
              }
              className={
                editor.isActive({ textAlign: 'justify' }) ? 'is-active' : ''
              }
            >
              Justify
            </button>
          </div>

          <div className="toolbar">
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
            >
              Insert Table
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .addColumnBefore()
                  .run()
              }
            >
              addColumnBefore
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .addColumnAfter()
                  .run()
              }
            >
              addColumnAfter
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .deleteColumn()
                  .run()
              }
            >
              deleteColumn
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .addRowBefore()
                  .run()
              }
            >
              addRowBefore
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .addRowAfter()
                  .run()
              }
            >
              addRowAfter
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .deleteRow()
                  .run()
              }
            >
              deleteRow
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .deleteTable()
                  .run()
              }
            >
              deleteTable
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .mergeCells()
                  .run()
              }
            >
              mergeCells
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .splitCell()
                  .run()
              }
            >
              splitCell
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHeaderColumn()
                  .run()
              }
            >
              toggleHeaderColumn
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHeaderRow()
                  .run()
              }
            >
              toggleHeaderRow
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleHeaderCell()
                  .run()
              }
            >
              toggleHeaderCell
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .mergeOrSplit()
                  .run()
              }
            >
              mergeOrSplit
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setCellAttribute('colspan', 2)
                  .run()
              }
            >
              setCellAttribute
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .fixTables()
                  .run()
              }
            >
              fixTables
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .goToNextCell()
                  .run()
              }
            >
              goToNextCell
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .goToPreviousCell()
                  .run()
              }
            >
              goToPreviousCell
            </button>

            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setFontFamily('Inter')
                  .run()
              }
              className={
                editor.isActive('textStyle', { fontFamily: 'Inter' })
                  ? 'is-active'
                  : ''
              }
              data-test-id="inter"
            >
              Inter
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setFontFamily('"Comic Sans MS", "Comic Sans"')
                  .run()
              }
              className={
                editor.isActive('textStyle', {
                  fontFamily: '"Comic Sans MS", "Comic Sans"'
                })
                  ? 'is-active'
                  : ''
              }
              data-test-id="comic-sans"
            >
              Comic Sans
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setFontFamily('serif')
                  .run()
              }
              className={
                editor.isActive('textStyle', { fontFamily: 'serif' })
                  ? 'is-active'
                  : ''
              }
              data-test-id="serif"
            >
              Serif
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setFontFamily('monospace')
                  .run()
              }
              className={
                editor.isActive('textStyle', { fontFamily: 'monospace' })
                  ? 'is-active'
                  : ''
              }
              data-test-id="monospace"
            >
              Monospace
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setFontFamily('cursive')
                  .run()
              }
              className={
                editor.isActive('textStyle', { fontFamily: 'cursive' })
                  ? 'is-active'
                  : ''
              }
              data-test-id="cursive"
            >
              Cursive
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setFontFamily('nunito')
                  .run()
              }
              className={
                editor.isActive('textStyle', {
                  fontFamily: 'nunito'
                })
                  ? 'is-active'
                  : ''
              }
              data-test-id="nunito"
            >
              Nunito
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .setFontFamily('"Exo 2"')
                  .run()
              }
              className={
                editor.isActive('textStyle', { fontFamily: '"Exo 2"' })
                  ? 'is-active'
                  : ''
              }
              data-test-id="exo2"
            >
              Exo 2
            </button>
            <button
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .unsetFontFamily()
                  .run()
              }
              data-test-id="unsetFontFamily"
            >
              Unset font family
            </button>
          </div>

          {/* TipTap Editor */}

          {/* Save & Delete Buttons */}
          <div className="toolbar">
            <div className="toolbar">
              <button onClick={saveContent} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Content'}
              </button>
            </div>

            <button onClick={getHtmlContent}>Get HTML</button>
          </div>
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  )
}

export default withMainLayout(Editor)
